import intl from "react-intl-universal";
import {
  formatFilterAmount,
  formatFilterDate,
  parseAmountInput,
  startOfDay,
  endOfDay,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  BAAS_STABLES_ASSETS,
  BAAS_STABLES_OPERATIONS,
  BAAS_STABLES_STATUSES,
  getBaasStableClientsFromTransactions,
} from "../../../../Services/baasStablesApi";

export const EMPTY_BAAS_STABLES_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  operations: [],
  statuses: [],
  assets: [],
  clientIds: [],
};

export const getBaasStableOperationOptions = () =>
  BAAS_STABLES_OPERATIONS.map((operation) => ({
    id: operation,
    label: intl.get(`baas.stables.operation.${operation}`),
  }));

export const getBaasStableStatusOptions = () =>
  BAAS_STABLES_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.stables.status.${status}`),
  }));

export const getBaasStableAssetOptions = () =>
  BAAS_STABLES_ASSETS.map((asset) => ({
    id: asset,
    label: asset,
  }));

export const getBaasStableClientOptions = (transactions = []) =>
  getBaasStableClientsFromTransactions(transactions).map((client) => ({
    id: client.id,
    label: client.name,
  }));

const matchesSearch = (transaction, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [
    transaction.referenceId,
    transaction.id,
    transaction.clientName,
    transaction.clientId,
    transaction.asset,
    transaction.metadata?.tx_hash,
    transaction.metadata?.wallet_from,
    transaction.metadata?.wallet_to,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (transaction, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const createdAt = new Date(transaction.createdAt);
  if (Number.isNaN(createdAt.getTime())) return false;
  if (dateFrom && createdAt < startOfDay(dateFrom)) return false;
  if (dateTo && createdAt > endOfDay(dateTo)) return false;
  return true;
};

const matchesAmountRange = (transaction, amountMin, amountMax) => {
  const minCents = parseAmountInput(amountMin);
  const maxCents = parseAmountInput(amountMax);
  const amount = Number(transaction.amount) || 0;
  if (minCents != null && amount < minCents) return false;
  if (maxCents != null && amount > maxCents) return false;
  return true;
};

export const filterBaasStablesTransactions = (items = [], filters = EMPTY_BAAS_STABLES_FILTERS) =>
  items.filter((transaction) => {
    if (!matchesSearch(transaction, filters.searchQuery)) return false;
    if (!matchesDateRange(transaction, filters.dateFrom, filters.dateTo)) return false;
    if (!matchesAmountRange(transaction, filters.amountMin, filters.amountMax)) return false;
    if (filters.operations?.length && !filters.operations.includes(transaction.operation)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(transaction.status)) return false;
    if (filters.assets?.length && !filters.assets.includes(transaction.asset)) return false;
    if (filters.clientIds?.length && !filters.clientIds.includes(transaction.clientId)) return false;
    return true;
  });

export const hasActiveBaasStablesFilters = (filters = EMPTY_BAAS_STABLES_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.amountMin ||
      filters.amountMax ||
      filters.operations?.length ||
      filters.statuses?.length ||
      filters.assets?.length ||
      filters.clientIds?.length,
  );

export const isBaasStablesChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return Boolean(filters.amountMin || filters.amountMax);
    case "operation":
      return Boolean(filters.operations?.length);
    case "status":
      return Boolean(filters.statuses?.length);
    case "asset":
      return Boolean(filters.assets?.length);
    case "client":
      return Boolean(filters.clientIds?.length);
    default:
      return false;
  }
};

export const buildBaasStablesFilterTags = (
  filters = EMPTY_BAAS_STABLES_FILTERS,
  transactions = [],
) => {
  const tags = [];
  const operationOptions = getBaasStableOperationOptions();
  const statusOptions = getBaasStableStatusOptions();
  const assetOptions = getBaasStableAssetOptions();
  const clientOptions = getBaasStableClientOptions(transactions);

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromLabel = filters.dateFrom ? formatFilterDate(filters.dateFrom) : "…";
    const toLabel = filters.dateTo ? formatFilterDate(filters.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (filters.amountMin || filters.amountMax) {
    const minLabel = filters.amountMin ? formatFilterAmount(filters.amountMin) : "…";
    const maxLabel = filters.amountMax ? formatFilterAmount(filters.amountMax) : "…";
    tags.push({
      key: "amount",
      iconType: "amount",
      value: `${minLabel} – ${maxLabel}`,
    });
  }

  if (filters.operations?.length) {
    tags.push({
      key: "operations",
      iconType: "type",
      value: filters.operations
        .map((id) => operationOptions.find((item) => item.id === id)?.label ?? id)
        .join(", "),
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((id) => statusOptions.find((item) => item.id === id)?.label ?? id)
        .join(", "),
    });
  }

  if (filters.assets?.length) {
    tags.push({
      key: "assets",
      iconType: "currency",
      value: filters.assets
        .map((id) => assetOptions.find((item) => item.id === id)?.label ?? id)
        .join(", "),
    });
  }

  if (filters.clientIds?.length) {
    tags.push({
      key: "clientIds",
      iconType: "subAccount",
      value: filters.clientIds
        .map((clientId) => clientOptions.find((item) => item.id === clientId)?.label ?? clientId)
        .join(", "),
    });
  }

  return tags;
};

export const removeBaasStablesFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "amount":
      return { ...filters, amountMin: "", amountMax: "" };
    case "operations":
      return { ...filters, operations: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "assets":
      return { ...filters, assets: [] };
    case "clientIds":
      return { ...filters, clientIds: [] };
    default:
      return filters;
  }
};
